/* The plugin detail drawer — one of exactly two overlays in the reference.
 *
 * A right-aligned panel over a scrim, `min(640px, 94vw)`, closed by a text "← Close" or by clicking
 * the scrim. Not the shared `Drawer`, which renders a different header and close affordance.
 *
 * **The install panel is what the feature exists for**, and its job is to make "declared" and
 * "granted" visibly different things. A plugin declares; a tenant grants a subset. Only the declared
 * set is offered, so the server's 422 naming undeclared codes is unreachable from here.
 *
 * **The upgrade panel names `not_carried` as *not granted*, never as "new".** An upgrade carries the
 * intersection of what was granted and what the target declares — a newer version cannot widen a
 * tenant's grant — and rendering that list the other way would invert the security property.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useConnectionNames } from '@/entities/connection';
import { useAggregateActivity } from '@/features/audit';
import { groupByConnection, useDatasets } from '@/features/catalog-browser';
import type { NameLookup } from '@/features/catalog-browser';
import type { Capability } from '@/shared/api/generated/marketplace';

import {
  useCapabilityCatalogue,
  useInstallations,
  useInstallPlugin,
  usePlugin,
  usePlugins,
  useRunPlugin,
  useUpgradePlan,
  useUpgradePlugin,
} from '../api/marketplace';
import {
  advisoryFindings,
  blockingFindings,
  capabilityLabelKey,
  installationFor,
  installBlockedReason,
  runBlockedReason,
  upgradeTargetFor,
} from '../model/marketplace';
import { Pill } from './atoms';
import type { Permissions } from './MarketplaceScreen';

export function PluginDrawer({
  pluginId,
  may,
  onClose,
}: {
  readonly pluginId: string;
  readonly may: Permissions;
  readonly onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const plugin = usePlugin(pluginId);
  const plugins = usePlugins();
  const installations = useInstallations();
  // The permission governing each capability is the server's own fact, served on
  // `/capabilities` as `governed_by`. It was previously a copy key per capability code — keys
  // that were never written, so every row printed the literal `marketplace.gate.dataset.schema`.
  // Restating a permission mapping in the copy catalogue would also have been a second
  // vocabulary for it, free to drift from the one the gate actually consults.
  const catalogue = useCapabilityCatalogue();
  const activity = useAggregateActivity('Plugin', pluginId);

  const [granting, setGranting] = useState<readonly Capability[] | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [running, setRunning] = useState(false);
  const [datasetId, setDatasetId] = useState('');

  const install = useInstallPlugin();
  const run = useRunPlugin();
  const upgrade = useUpgradePlugin();

  const current = plugin.data;
  const installation =
    current === undefined
      ? undefined
      : installationFor(current, installations.data ?? []);
  const target =
    current === undefined ? undefined : upgradeTargetFor(current, plugins.data ?? []);
  const plan = useUpgradePlan(
    upgrading && target !== undefined ? pluginId : null,
    upgrading && target !== undefined ? target.version : null,
  );

  return (
    <div
      className="lq-mp__scrim"
      role="presentation"
      onClick={() => {
        onClose();
      }}
    >
      <div
        className="lq-mp__drawer"
        role="dialog"
        aria-label={t('marketplace.detail.close')}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <button type="button" className="lq-mp__back" onClick={onClose}>
          {t('marketplace.detail.close')}
        </button>

        {current === undefined ? (
          <div className="lq-mp__reason">
            {plugin.isError
              ? t('marketplace.error.pluginBody')
              : t('marketplace.detail.loading')}
          </div>
        ) : (
          <>
            <div className="lq-mp__drawerName">
              {current.name} v{current.version}
            </div>
            <div className="lq-mp__drawerHash">{current.artifact_hash}</div>
            <div className="lq-mp__drawerSub">
              {t('marketplace.detail.submittedAt', {
                when: new Date(current.submitted_at).toLocaleString(),
              })}
            </div>

            <div className="lq-mp__pills">
              <Pill
                tone={
                  current.review_verdict === 'pass'
                    ? 'ok'
                    : current.review_verdict === 'fail'
                      ? 'error'
                      : 'unknown'
                }
                label={t('marketplace.detail.reviewIs', {
                  verdict: t(`marketplace.verdict.${current.review_verdict}`),
                })}
              />
              {current.state === 'revoked' ? (
                <span className="lq-mp__tag lq-mp__tag--error">
                  {t('marketplace.state.revoked')}
                </span>
              ) : null}
              {installation !== undefined ? (
                <span
                  className={
                    installation.enabled
                      ? 'lq-mp__tag lq-mp__tag--ok'
                      : 'lq-mp__tag lq-mp__tag--muted'
                  }
                >
                  {installation.enabled
                    ? t('marketplace.detail.installedEnabled')
                    : t('marketplace.detail.installedDisabled')}
                </span>
              ) : null}
            </div>

            {blockingFindings(current).map((finding) => (
              <div className="lq-mp__rf" key={finding.code}>
                <div className="lq-mp__rfHead">
                  {t('marketplace.finding.blocking', { code: finding.code })}
                </div>
                <div className="lq-mp__rfBody">{finding.detail}</div>
              </div>
            ))}
            {advisoryFindings(current).map((finding) => (
              <div className="lq-mp__rf lq-mp__rf--advisory" key={finding.code}>
                <div className="lq-mp__rfHead lq-mp__rfHead--advisory">
                  {t('marketplace.finding.advisory', { code: finding.code })}
                </div>
                <div className="lq-mp__rfBody">{finding.detail}</div>
              </div>
            ))}

            {!current.runnable ? (
              <div className="lq-mp__unrunnable">
                {t(
                  installBlockedReason(current) ??
                    'marketplace.blocked.install.unpublished',
                )}
              </div>
            ) : null}

            <div className="lq-mp__eyebrow lq-mp__eyebrow--spaced">
              {t('marketplace.detail.declared')}
            </div>
            {current.declared_capabilities.map((code) => {
              const granted = installation?.granted_capabilities.includes(code) ?? false;
              // Undefined while the catalogue is loading, or if it fails: the line is omitted
              // rather than rendered empty, because a gate line with nothing after it reads as
              // "governed by nothing".
              const governedBy = catalogue.data?.capabilities.find(
                (entry) => entry.code === code,
              )?.governed_by;
              return (
                <div className="lq-mp__declRow" key={code}>
                  <span className="lq-mp__declCode">{code}</span>
                  <span className="lq-mp__declDesc">
                    {t(capabilityLabelKey(code))}
                    {governedBy === undefined ? null : (
                      <span className="lq-mp__declGate">
                        {t('marketplace.boundary.gatedBy')}{' '}
                        <span className="lq-mp__mono">{governedBy}</span>
                      </span>
                    )}
                  </span>
                  {granted ? (
                    <span className="lq-mp__grantedTag">
                      {t('marketplace.detail.granted')}
                    </span>
                  ) : null}
                </div>
              );
            })}

            {installation !== undefined ? (
              <>
                <div className="lq-mp__grantLine">
                  {t('marketplace.detail.grantLine', {
                    granted: installation.granted_capabilities.length,
                    declared: current.declared_capabilities.length,
                  })}
                </div>
                <div className="lq-mp__installedBy">
                  {t('marketplace.detail.installedBy', {
                    who: installation.installed_by,
                    when: new Date(installation.installed_at).toLocaleString(),
                  })}
                </div>
              </>
            ) : null}

            {/* ---- install ---- */}
            {installation === undefined &&
            may.install &&
            installBlockedReason(current) === null ? (
              granting === null ? (
                <button
                  type="button"
                  className="lq-mp__btn lq-mp__btn--lg"
                  onClick={() => {
                    setGranting(current.declared_capabilities);
                  }}
                >
                  {t('marketplace.action.install')}
                </button>
              ) : (
                <div className="lq-mp__grant">
                  <div className="lq-mp__formTitle">{t('marketplace.install.title')}</div>
                  {current.declared_capabilities.map((code) => (
                    <label className="lq-mp__grantCheck" key={code}>
                      <input
                        type="checkbox"
                        checked={granting.includes(code)}
                        onChange={(event) => {
                          // A stored mutation error describes the request that failed, not the
                          // selection now on screen. Left standing while somebody edits the boxes,
                          // it reads as a refusal of what they have just chosen — which is exactly
                          // how "grants at least one capability" stayed visible with two ticked.
                          install.reset();
                          setGranting(
                            event.target.checked
                              ? [...granting, code]
                              : granting.filter((each) => each !== code),
                          );
                        }}
                      />
                      <span>
                        <span className="lq-mp__capCode">{code}</span>
                        <span className="lq-mp__grantDesc">
                          {t(capabilityLabelKey(code))}
                        </span>
                      </span>
                    </label>
                  ))}
                  <div className="lq-mp__grantNote">
                    {t('marketplace.install.countLine', {
                      granted: granting.length,
                      declared: current.declared_capabilities.length,
                    })}
                  </div>
                  {granting.length === 0 ? (
                    <div className="lq-mp__grantWarn">
                      {t('marketplace.install.emptyGrant')}
                    </div>
                  ) : null}
                  <div className="lq-mp__grantNote">
                    {t('marketplace.install.enablesImmediately')}
                  </div>
                  {install.isError ? (
                    <div className="lq-mp__grantWarn">{install.error.message}</div>
                  ) : null}
                  <div className="lq-mp__btnRow">
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--primary"
                      disabled={granting.length === 0 || install.isPending}
                      onClick={() => {
                        install.mutate(
                          { pluginId, capabilities: granting },
                          {
                            onSuccess: () => {
                              setGranting(null);
                            },
                          },
                        );
                      }}
                    >
                      {t('marketplace.install.confirm', { count: granting.length })}
                    </button>
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--md"
                      onClick={() => {
                        setGranting(null);
                      }}
                    >
                      {t('marketplace.action.cancel')}
                    </button>
                  </div>
                </div>
              )
            ) : null}

            {/* ---- run ---- */}
            {installation !== undefined && may.run ? (
              running ? (
                <div className="lq-mp__form">
                  <div className="lq-mp__formTitle">{t('marketplace.run.title')}</div>
                  <div className="lq-mp__runTarget">
                    {current.name} v{current.version}
                  </div>
                  <div className="lq-mp__formSub">
                    {t('marketplace.installed.grantedList', {
                      list: installation.granted_capabilities.join(', '),
                    })}
                  </div>
                  <DatasetChoice
                    value={datasetId}
                    onChange={(chosen) => {
                      run.reset();
                      setDatasetId(chosen);
                    }}
                  />
                  {run.isError ? (
                    <div className="lq-mp__err">{run.error.message}</div>
                  ) : null}
                  {run.data !== undefined ? (
                    <div
                      className={
                        run.data.status === 'succeeded'
                          ? 'lq-mp__note lq-mp__note--ok'
                          : 'lq-mp__note'
                      }
                    >
                      {t('marketplace.run.outcome', {
                        status: t(`marketplace.runStatus.${run.data.status}`),
                        findings: run.data.findings.length,
                      })}
                    </div>
                  ) : null}
                  <div className="lq-mp__btnRow">
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--primary"
                      disabled={datasetId.trim() === '' || run.isPending}
                      onClick={() => {
                        run.mutate({ pluginId, datasetId: datasetId.trim() });
                      }}
                    >
                      {t('marketplace.action.run')}
                    </button>
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--md"
                      onClick={() => {
                        setRunning(false);
                      }}
                    >
                      {t('marketplace.action.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="lq-mp__btn lq-mp__btn--lg"
                    disabled={runBlockedReason(current, installation) !== null}
                    onClick={() => {
                      setRunning(true);
                    }}
                  >
                    {t('marketplace.action.run')}
                  </button>
                  {runBlockedReason(current, installation) !== null ? (
                    <div className="lq-mp__reason">
                      {t(runBlockedReason(current, installation) ?? '')}
                    </div>
                  ) : null}
                </>
              )
            ) : null}

            {/* ---- upgrade ---- */}
            {installation !== undefined && target !== undefined && may.install ? (
              upgrading ? (
                <div className="lq-mp__grant">
                  <div className="lq-mp__formTitle">
                    {t('marketplace.upgrade.title', {
                      from: current.version,
                      to: target.version,
                    })}
                  </div>
                  {plan.data !== undefined ? (
                    <>
                      <div className="lq-mp__upLine">
                        {t('marketplace.upgrade.carried', {
                          list:
                            plan.data.carried_capabilities.join(', ') ||
                            t('marketplace.upgrade.none'),
                        })}
                      </div>
                      <div className="lq-mp__upLine lq-mp__upLine--warn">
                        {t('marketplace.upgrade.notCarried', {
                          list:
                            plan.data.not_carried_capabilities.join(', ') ||
                            t('marketplace.upgrade.none'),
                        })}
                      </div>
                      <div className="lq-mp__upLine lq-mp__upLine--error">
                        {t('marketplace.upgrade.dropped', {
                          list:
                            plan.data.dropped_capabilities.join(', ') ||
                            t('marketplace.upgrade.none'),
                        })}
                      </div>
                      <div className="lq-mp__upNote">
                        {t('marketplace.upgrade.enabledCarries')}
                      </div>
                    </>
                  ) : null}
                  {plan.isError ? (
                    <div className="lq-mp__err">{plan.error.message}</div>
                  ) : null}
                  {upgrade.isError ? (
                    <div className="lq-mp__err">{upgrade.error.message}</div>
                  ) : null}
                  <div className="lq-mp__btnRow">
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--primary"
                      disabled={plan.data === undefined || upgrade.isPending}
                      onClick={() => {
                        upgrade.mutate(
                          { pluginId, toVersion: target.version },
                          {
                            onSuccess: () => {
                              setUpgrading(false);
                            },
                          },
                        );
                      }}
                    >
                      {t('marketplace.upgrade.confirm')}
                    </button>
                    <button
                      type="button"
                      className="lq-mp__btn lq-mp__btn--md"
                      onClick={() => {
                        setUpgrading(false);
                      }}
                    >
                      {t('marketplace.action.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="lq-mp__btn lq-mp__btn--lg"
                  onClick={() => {
                    setUpgrading(true);
                  }}
                >
                  {t('marketplace.action.upgrade', { version: target.version })}
                </button>
              )
            ) : null}

            <div className="lq-mp__eyebrow lq-mp__eyebrow--spaced">
              {t('marketplace.detail.activity')}
            </div>
            {(activity.data?.items ?? []).length === 0 ? (
              <div className="lq-mp__reason">{t('marketplace.detail.noActivity')}</div>
            ) : (
              (activity.data?.items ?? []).map((entry) => (
                <div
                  className="lq-mp__da"
                  key={`${entry.chain_date}-${String(entry.sequence)}`}
                >
                  <span className="lq-mp__daAt">
                    {new Date(entry.occurred_at).toLocaleDateString()}
                  </span>
                  <span className="lq-mp__daDetail">
                    {t(`marketplace.audit.${entry.action}`)} · {entry.actor}
                  </span>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Which dataset to run against, chosen by name.
 *
 * **This replaced a text field asking for a UUID**, which is not something anybody has to hand: a
 * dataset id appears in a URL and nowhere a person reads, so the field could only be filled by
 * copying from another screen — and a mistyped one is a 404 that reads as a broken plugin. The
 * defect this codebase has recorded four times is rendering a bare id where a name belongs; asking
 * for one is the same mistake with the work pushed onto the reader.
 *
 * **Grouped by source through `catalog-browser`'s own `groupByConnection`**, never a local
 * grouping. That function holds the four-label decision a heading needs — named, loading,
 * unreadable, unlisted — and a second implementation is how two screens come to disagree about
 * what a missing connection name means. `LineagePicker` pairs the same two for the same reason.
 *
 * **A `<select>` rather than the searchable index `/lineage` uses**, and that is a judgement about
 * this surface rather than a disagreement with that one. `LineagePicker`'s own docstring explains
 * why it replaced a dropdown: a steward arriving at lineage does not yet know which tables *have*
 * lineage, so the list has to carry per-row facts. Here the reader has already decided to run a
 * plugin and is naming a table they have in mind, and this panel is a block inside a drawer with no
 * room for an index. Nothing about the choice is per-row.
 *
 * **The bound is on screen.** This is the catalogue's first page, not the estate, and a picker that
 * silently omits a dataset is worse than one that says what it lists. Loading, unreadable and empty
 * are three separate states for the reason every absence here is: a failed read must not render as
 * an estate with no datasets in it.
 */
const PICKER_DATASETS = 200;

function DatasetChoice({
  value,
  onChange,
}: {
  readonly value: string;
  readonly onChange: (datasetId: string) => void;
}): ReactElement {
  const { t } = useTranslation();
  const datasets = useDatasets({ search: '', connectionId: null });
  const connections = useConnectionNames();

  const lookup: NameLookup = {
    status: connections.isPending
      ? 'loading'
      : connections.isError
        ? 'unreadable'
        : 'ready',
    names: connections.data ?? new Map<string, string>(),
  };
  const rows = datasets.data?.items ?? [];

  if (datasets.isPending) {
    return <div className="lq-mp__reason">{t('marketplace.run.datasetsLoading')}</div>;
  }
  if (datasets.isError) {
    return <div className="lq-mp__err">{t('marketplace.run.datasetsUnreadable')}</div>;
  }
  if (rows.length === 0) {
    return <div className="lq-mp__reason">{t('marketplace.run.datasetsEmpty')}</div>;
  }

  return (
    <>
      <label className="lq-mp__label" htmlFor="lq-mp-dataset">
        {t('marketplace.run.datasetLabel')}
      </label>
      <select
        id="lq-mp-dataset"
        className="lq-mp__select lq-mp__select--wide"
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
      >
        <option value="">{t('marketplace.run.datasetUnchosen')}</option>
        {groupByConnection(rows, lookup).map((group) => (
          <optgroup
            key={group.connectionId}
            label={
              group.label.kind === 'named'
                ? group.label.name
                : t(`catalog.group.${group.label.kind}`)
            }
          >
            {group.rows.map((row) => (
              <option key={row.id} value={row.id}>
                {row.schema_name}.{row.object_name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <div className="lq-mp__reason">
        {t('marketplace.run.datasetsNote', { limit: PICKER_DATASETS })}
      </div>
    </>
  );
}
